/**
 * The Command Center data-source seam + pure derivations. All dashboard metrics/cards come from these
 * functions applied to a real `AtlasDataset` — never from hardcoded values (directive §8, §13).
 *
 * `Availability<T>` is the honesty primitive: a value the engine can supply is `{available:true,value}`;
 * a value the engine cannot yet derive is `{available:false, reason}` — the UI renders that as an
 * explicit "not yet derived" state rather than a fabricated number (directive §8, §12).
 */

import type { AtlasDataset, Evidence, Finding } from "./model";

export type Availability<T> = { readonly available: true; readonly value: T } | { readonly available: false; readonly reason: string };

export const available = <T>(value: T): Availability<T> => ({ available: true, value });
export const unavailable = <T>(reason: string): Availability<T> => ({ available: false, reason });

/** The top-of-TODAY environment summary. Confirmed-real counters are plain numbers; findings/changes are
 *  Availability because they depend on layers a run may not have produced. */
export interface EnvironmentSummary {
  readonly assetsObserved: number;
  readonly evidenceRecords: number;
  readonly relationships: number;
  readonly verifiedEvidence: number;
  /** Everything not `verified` (unverified + stale + rejected) — labelled "not verified" in the UI. */
  readonly notVerifiedEvidence: number;
  readonly activeFindings: Availability<number>;
  readonly changesSinceLastObservation: Availability<number>;
  readonly lastObserved: string | null;
  readonly lastVerified: string | null;
}

export function deriveEnvironmentSummary(d: AtlasDataset): EnvironmentSummary {
  const verified = d.evidence.filter((e) => e.verification === "verified").length;
  return {
    assetsObserved: d.entities.length,
    evidenceRecords: d.evidence.length,
    relationships: d.relationships.length,
    verifiedEvidence: verified,
    notVerifiedEvidence: d.evidence.length - verified,
    activeFindings: d.findings ? available(d.findings.filter((f) => f.status === "active").length) : unavailable("no assessment run loaded"),
    changesSinceLastObservation: d.changes ? available(d.changes.length) : unavailable("no prior observation to diff against"),
    lastObserved: d.observedAt ?? null,
    lastVerified: d.verifiedAt ?? null,
  };
}

/** Active findings ranked for the TODAY intelligence cards — severity first, then most-recently observed.
 *  Pure ordering over real findings; it neither invents nor reweights beyond what the finding carries. */
const SEVERITY_RANK: Record<Finding["severity"], number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
export function rankFindings(findings: readonly Finding[]): readonly Finding[] {
  return [...findings]
    .filter((f) => f.status === "active")
    .sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || (b.lastObserved ?? "").localeCompare(a.lastObserved ?? ""));
}

/** Evidence supporting a subject (entity/relationship/finding), for the evidence drawer. Pure filter. */
export function evidenceForSubject(d: AtlasDataset, subjectId: string): readonly Evidence[] {
  return d.evidence.filter((e) => e.subjectId === subjectId);
}
export function evidenceByIds(d: AtlasDataset, ids: readonly string[]): readonly Evidence[] {
  const set = new Set(ids);
  return d.evidence.filter((e) => set.has(e.id));
}

/** The async boundary the UI depends on — swappable between the (empty) memory-run store and a
 *  discovery-snapshot-backed source without any UI change. */
export interface AtlasDataSource {
  load(): Promise<AtlasDataset>;
}

export const emptyDataset = (): AtlasDataset => ({ entities: [], evidence: [], relationships: [] });

/** Honest default when no run is loaded — the current reality of the memory store. Renders as real
 *  zeros + "nothing observed yet", never fabricated intelligence. */
export const emptyDataSource: AtlasDataSource = { load: () => Promise.resolve(emptyDataset()) };

export function isObserved(d: AtlasDataset): boolean {
  return d.entities.length > 0 || d.evidence.length > 0;
}
